import React from 'react';
import { motion } from 'framer-motion';
import { Globe, MapPin, Building, ChevronRight } from 'lucide-react';

const FinancialInnovationShowcase = () => {
  const projects = {
    zones: [
      {
        name: 'UEMOA',
        type: 'Zone Économique',
        description: 'Union Économique et Monétaire Ouest-Africaine',
        gradient: 'from-blue-600 to-blue-400',
        borderHover: 'hover:border-blue-500',
        initiatives: [
          { name: 'Trade Finance Blockchain', link: '/r&d/uemoa/blockchain' },
          { name: 'RTGS Enhancement', link: '/r&d/uemoa/rtgs' },
          { name: 'Open Banking', link: '/r&d/uemoa/openbanking' }
        ],
        countries: ['Sénégal', 'Mali', 'Burkina Faso', 'Niger', 'Côte d\'Ivoire', 'Guinée-Bissau', 'Bénin', 'Togo']
      },
      {
        name: 'CEMAC',
        type: 'Zone Économique',
        description: 'Communauté Économique et Monétaire de l\'Afrique Centrale',
        gradient: 'from-emerald-600 to-emerald-400',
        borderHover: 'hover:border-emerald-500',
        initiatives: [
          { name: 'GIMAC Modernization', link: '/r&d/cemac/gimac' },
          { name: 'Digital Finance Framework', link: '/r&d/cemac/finance' }
        ],
        countries: ['Cameroun', 'RCA', 'Tchad', 'Congo', 'Guinée Équatoriale', 'Gabon']
      },
    {
     name: 'SADC',
     type: 'Zone Régionale', 
     description: 'Paiements instantanés transfrontaliers régionaux',
     gradient: 'from-sky-600 to-sky-400',
     softGradient: 'from-sky-50 to-sky-100/50',
     accentColor: 'sky',
     stats: { pays: '8 pays', timeline: '2024', type: 'Pilote' },
     initiatives: [
       { name: 'TCIB', link: '/r&d/sadc/tcib', description: 'Transactions Cleared on Immediate Basis' }
     ],
     countries: ['Afrique du Sud', 'Botswana', 'Lesotho', 'Namibie', 'eSwatini', 'Zimbabwe', 'Mozambique', 'Zambie']
   },
   {
     name: 'COMESA',
     type: 'Zone Régionale',
     description: 'Common Market for Eastern and Southern Africa',
     gradient: 'from-violet-600 to-violet-400',
     softGradient: 'from-violet-50 to-violet-100/50',
     accentColor: 'violet',
     stats: { pays: '9 pays', timeline: '2024-2025', focus: 'Paiements régionaux' },
     initiatives: [
       { name: 'Regional Payment System', link: '/r&d/comesa/rps', description: 'Système paiements régional' },
       { name: 'API Standards', link: '/r&d/comesa/api', description: 'Standards API communs' }
     ]
   }
 ],
    countries: [
      {
        name: 'Kenya',
        type: 'National',
        description: 'Programme d\'innovation financière nationale',
        gradient: 'from-purple-600 to-purple-400',
        borderHover: 'hover:border-purple-500',
        initiatives: [
          { name: 'Digital Finance Transformation', link: '/r&d/kenya/finance' }
        ]
      },
      {
        name: 'Maroc',
        type: 'National',
        description: 'Initiative Open Banking nationale',
        gradient: 'from-red-600 to-red-400',
        borderHover: 'hover:border-red-500',
        initiatives: [
          { name: 'Open Banking Framework', link: '/r&d/maroc/openbanking' }
        ]
      },
      {
        name: 'Sénégal',
        type: 'National',
        description: 'Programme de transformation digitale',
        gradient: 'from-amber-600 to-amber-400',
        borderHover: 'hover:border-amber-500',
        initiatives: [
          { name: 'Smart Sénégal', link: '/r&d/senegal/smart' }
        ]
      },
      {
        name: 'Mali',
        type: 'National',
        description: 'Infrastructure financière digitale',
        gradient: 'from-lime-600 to-lime-400',
        borderHover: 'hover:border-lime-500',
        initiatives: [
          { name: 'Digital Mali', link: '/r&d/mali/digital' }
        ]
      }
    ],
    worldwide: [
      {
        name: 'CBPR+',
        type: 'Mondial',
        description: 'Cross-Border Payments & Reporting Plus',
        gradient: 'from-gray-800 to-gray-600',
        borderHover: 'hover:border-gray-500',
        initiatives: [
          { name: 'Standards Analysis', link: '/r&d/world/cbpr' }
        ]
      }
    ]
  };

  const CardIcon = ({ type }) => {
    switch (type) {
      case 'Zone Économique':
        return <Building className="w-6 h-6" />;
      case 'National':
        return <MapPin className="w-6 h-6" />;
      case 'Mondial':
        return <Globe className="w-6 h-6" />;
      default:
        return null;
    }
  };

  const ProjectCard = ({ project }) => (
    <motion.div
      whileHover={{ y: -5 }}
      className={`bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 ${project.borderHover} transition-all duration-300`}
    >
      <div className={`h-2 bg-gradient-to-r ${project.gradient}`} />
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg bg-gradient-to-br ${project.gradient} text-white`}>
              <CardIcon type={project.type} />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{project.name}</h3>
              <p className="text-sm text-gray-600">{project.type}</p>
            </div>
          </div>
        </div>

        <p className="text-gray-600 mb-4">
          {project.description}
        </p>

        {project.countries && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-500 mb-2">Pays membres</h4>
            <div className="flex flex-wrap gap-2">
              {project.countries.map(country => (
                <span key={country} className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-600">
                  {country}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="space-y-2">
          {project.initiatives.map((initiative, idx) => (
            <a
              key={idx}
              href={initiative.link}
              className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 group"
            >
              <span className="text-gray-700 group-hover:text-gray-900">
                {initiative.name}
              </span>
              <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600" />
            </a>
          ))}
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Innovations Financières en Afrique
          </h1>
          <p className="text-xl text-gray-600">
            Explorer les initiatives majeures de transformation digitale
          </p>
        </div>

        <div className="space-y-16">
          {/* Zones Économiques */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Zones Économiques
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {projects.zones.map((project, idx) => (
                <ProjectCard key={idx} project={project} />
              ))}
            </div>
          </section>

          {/* Pays */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Initiatives Nationales
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.countries.map((project, idx) => (
                <ProjectCard key={idx} project={project} />
              ))}
            </div>
          </section>

          {/* Mondial */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Standards Internationaux
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.worldwide.map((project, idx) => (
                <ProjectCard key={idx} project={project} />
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default FinancialInnovationShowcase;